// Keyset cursors (04 §Pagination "opaque, signed, company-bound"; review #5). A cursor is
// `base64url(JSON {v, co, at, id}) "." base64url(HMAC-SHA256)`. It names the company it was
// issued for and the ordering key of the last row served; the MAC makes both tamper-proof. The
// key is a server secret injected at runtime (S1-08 config): this module only defines the
// format and takes the key as an argument, so the contracts hold no secret.
//
// Rows are ordered by (occurred_at DESC, event_id DESC), the SQL form being
// `(occurred_at, event_id) < ($at::timestamptz, $id)`. `at` is carried as the original
// timestamp string and compared at microsecond precision (`timestampMicros`), never through
// `Date.parse`, so rows that differ below a millisecond keep their database order.
import { z } from "zod";
import { compareTimestamps, Timestamp, Uuid } from "./primitives";

const CURSOR_VERSION = 1;

const CursorBody = z.strictObject({
  v: z.literal(CURSOR_VERSION),
  co: Uuid,
  at: Timestamp,
  id: Uuid,
});

export type KeysetKey = { occurred_at: string; event_id: string };

const enc = new TextEncoder();

function toBase64Url(bytes: Uint8Array): string {
  let bin = "";
  for (const byte of bytes) bin += String.fromCharCode(byte);
  return btoa(bin).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}

function fromBase64Url(s: string): Uint8Array<ArrayBuffer> {
  if (!/^[A-Za-z0-9_-]*$/.test(s)) throw new Error("not base64url");
  const bin = atob(s.replaceAll("-", "+").replaceAll("_", "/"));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

/** Imports the server's cursor secret (≥ 32 bytes) as an HMAC-SHA256 key. */
export function importCursorKey(secret: Uint8Array<ArrayBuffer> | string): Promise<CryptoKey> {
  const raw = typeof secret === "string" ? enc.encode(secret) : secret;
  if (raw.byteLength < 32) throw new Error("cursor secret must be at least 32 bytes");
  return crypto.subtle.importKey("raw", raw, { name: "HMAC", hash: "SHA-256" }, false, [
    "sign",
    "verify",
  ]);
}

/** Signs the key of the last row of a page as the next cursor. */
export async function encodeCursor(
  key: CryptoKey,
  companyId: string,
  last: KeysetKey,
): Promise<string> {
  const body = CursorBody.parse({
    v: CURSOR_VERSION,
    co: companyId,
    at: last.occurred_at,
    id: last.event_id,
  });
  const payload = toBase64Url(enc.encode(JSON.stringify(body)));
  const mac = new Uint8Array(await crypto.subtle.sign("HMAC", key, enc.encode(payload)));
  return `${payload}.${toBase64Url(mac)}`;
}

/**
 * Verifies and decodes a cursor for `companyId`. Null (→ `invalid_cursor`) when it is malformed,
 * its MAC does not verify (tampered, or signed with another key), or it names another company.
 */
export async function decodeCursor(
  key: CryptoKey,
  companyId: string,
  cursor: string,
): Promise<KeysetKey | null> {
  try {
    const [payload, mac, ...rest] = cursor.split(".");
    if (!payload || !mac || rest.length > 0) return null;
    const ok = await crypto.subtle.verify("HMAC", key, fromBase64Url(mac), enc.encode(payload));
    if (!ok) return null;
    const json = new TextDecoder("utf-8", { fatal: true }).decode(fromBase64Url(payload));
    const body = CursorBody.safeParse(JSON.parse(json));
    if (!body.success || body.data.co !== companyId) return null;
    return { occurred_at: body.data.at, event_id: body.data.id };
  } catch {
    return null;
  }
}

/** Newest first: by instant (µs, offsets normalized), ties by event id. Negative when a sorts first. */
export function compareKeysetDesc(a: KeysetKey, b: KeysetKey): number {
  const byTime = compareTimestamps(b.occurred_at, a.occurred_at);
  if (byTime !== 0) return byTime;
  return a.event_id < b.event_id ? 1 : a.event_id > b.event_id ? -1 : 0;
}

/**
 * Reference keyset page over rows already scoped to the company (the API's SQL must agree with
 * it; S1-08 tests against this). Returns `invalid_cursor` instead of throwing.
 */
export async function keysetPage<T extends KeysetKey>(
  key: CryptoKey,
  companyId: string,
  rows: readonly T[],
  query: { cursor?: string | undefined; limit?: number | undefined },
): Promise<{ data: T[]; next_cursor: string | null } | { error: "invalid_cursor" }> {
  const limit = query.limit ?? 50;
  let after: KeysetKey | null = null;
  if (query.cursor !== undefined) {
    after = await decodeCursor(key, companyId, query.cursor);
    if (!after) return { error: "invalid_cursor" };
  }
  const from = after;
  const ordered = [...rows]
    .sort(compareKeysetDesc)
    .filter((r) => from === null || compareKeysetDesc(r, from) > 0);
  const data = ordered.slice(0, limit);
  const last = data.at(-1);
  const next_cursor =
    ordered.length > limit && last ? await encodeCursor(key, companyId, last) : null;
  return { data, next_cursor };
}
